import AssessmentCumulativeTrendsChart from '@components/Charts/AssessmentCumulativeTrendsChart'
import AssessmentTrendByIntervalChart from '@components/Charts/AssessmentTrendByIntervalChart'
import { Button } from '@components/ui'
import { Row } from '@tanstack/react-table'
import {
  commonActionColumn,
  renderGradeColumn,
  renderHeaderTooltip
} from '@utils/assessmentReportUtils'

import { Database_Procs_TeamActivity_Item } from '@suntex.fim/api-client'
import { ColumnType } from '@suntex.fim/ui'

import {
  generateScrollableAssessmentCommonColumns,
  generateScrollableAssessmentCommonColumnsForSchool,
  renderWrappedAssessmentHeader
} from './CommonColumns'

const ACTIVITY_COLUMN_SIZE = 110
const TREND_COLUMN_SIZE = 250

const dailyTrendByIntervalLabels = [
  '6 days ago',
  '5 days ago',
  '4 days ago',
  '3 days ago',
  '2 days ago',
  '1 day ago',
  'Today'
]

const weeklyTrendByIntervalLabels = [
  '3 weeks ago',
  '2 weeks ago',
  '1 week ago',
  'This Week'
]

const monthlyTrendByIntervalLabels = [
  '4 months ago',
  '3 months ago',
  '2 months ago',
  '1 month ago',
  'This Month'
]

const dailyCumulativeTrendLabels = [
  '7 days ago',
  '6 days ago',
  '5 days ago',
  '4 days ago',
  '3 days ago',
  '2 days ago',
  '1 day ago',
  'Right Now'
]

const weeklyCumulativeTrendLabels = [
  '4 weeks ago',
  '3 weeks ago',
  '2 weeks ago',
  '1 week ago',
  'Right Now'
]

const monthlyCumulativeTrendLabels = [
  '5 months ago',
  '4 months ago',
  '3 months ago',
  '2 months ago',
  '1 month ago',
  'Right Now'
]
/**
 * handles hidable columns list
 */
export const hidableSelectLabel: Record<string, string> = {
  userId: 'User ID',
  displayName: 'Display Name',
  timeOnTask: 'Est. Time on Task (H)',
  stickers: 'Stickers',
  today: 'Today',
  thisWeek: 'This Week',
  stickers1W: '1 Week ago',
  stickers2W: '2 Weeks ago',
  stickers3W: '3 Weeks ago',
  dailyTrend: 'Daily Trend',
  weeklyTrend: 'Weekly Trend',
  monthlyTrend: 'Monthly Trend'
}
/**
 * returns JSX for sticker activity report table
 */
export function columns({
  view,
  visibleThisWeekColumns,
  setVisibleThisWeekColumns,
  sendMessage
}: {
  view: number
  visibleThisWeekColumns: boolean
  setVisibleThisWeekColumns: React.Dispatch<React.SetStateAction<boolean>>
  sendMessage?: (t: Row<Database_Procs_TeamActivity_Item>[]) => void
}) {
  /**
   * sets visibleThisWeekColumns state
   */
  const ToggleThisWeekColumns = () => {
    setVisibleThisWeekColumns(!visibleThisWeekColumns)
  }
  let actioncolumn: any = {}
  if (sendMessage) {
    actioncolumn = commonActionColumn<Database_Procs_TeamActivity_Item>(
      hidableSelectLabel,
      sendMessage
    )
  }

  /**
   * handles this week column mapping
   */
  const thisWeekColumns: ColumnType<Database_Procs_TeamActivity_Item>[] =
    visibleThisWeekColumns
      ? [
          {
            accessorFn: ({ stickers = 0, stickers1W = 0 }) =>
              stickers - stickers1W,
            id: 'thisWeek',
            cell: (info) => info.getValue(),
            header: () => {
              return (
                <Button
                  className="header-fill"
                  intent="icon"
                  onClick={() => ToggleThisWeekColumns()}
                >
                  {renderHeaderTooltip(' This Week')}
                </Button>
              )
            },
            size: ACTIVITY_COLUMN_SIZE
          },
          {
            header: () => renderHeaderTooltip('1 Day ago'),
            accessorFn: ({ stickers1D = 0, stickers2D = 0 }) =>
              stickers1D - stickers2D,
            id: 'stickers1D',
            size: ACTIVITY_COLUMN_SIZE
          },
          {
            header: () => renderHeaderTooltip('2 Days ago'),
            accessorFn: ({ stickers2D = 0, stickers3D = 0 }) =>
              stickers2D - stickers3D,
            id: 'stickers2D',
            size: ACTIVITY_COLUMN_SIZE
          },
          {
            header: () => renderHeaderTooltip('3 Days ago'),
            accessorFn: ({ stickers3D = 0, stickers4D = 0 }) =>
              stickers3D - stickers4D,
            id: 'stickers3D',
            size: ACTIVITY_COLUMN_SIZE
          },
          {
            header: () => renderHeaderTooltip('4 Days ago'),
            accessorFn: ({ stickers4D = 0, stickers5D = 0 }) =>
              stickers4D - stickers5D,
            id: 'stickers4D',
            size: ACTIVITY_COLUMN_SIZE
          },
          {
            header: () => renderHeaderTooltip('5 Days ago'),
            accessorFn: ({ stickers5D = 0, stickers6D = 0 }) =>
              stickers5D - stickers6D,
            id: 'stickers5D',
            size: ACTIVITY_COLUMN_SIZE
          }
        ]
      : [
          {
            accessorFn: ({ stickers = 0, stickers1W = 0 }) =>
              stickers - stickers1W,
            id: 'thisWeek',
            cell: (info) => info.getValue(),
            header: () => {
              return (
                <Button
                  className="header-fill"
                  intent="icon"
                  onClick={() => ToggleThisWeekColumns()}
                >
                  {renderHeaderTooltip('This Week')}
                </Button>
              )
            },
            size: ACTIVITY_COLUMN_SIZE
          }
        ]
  const highLightIds = visibleThisWeekColumns
    ? thisWeekColumns.map(({ id }) => id || '')
    : []
  const trendsColumns: ColumnType<Database_Procs_TeamActivity_Item>[] =
    view === 1
      ? [
          {
            header: () => renderHeaderTooltip('Daily Trend'),
            id: 'dailyTrend',
            size: TREND_COLUMN_SIZE,
            cell: (info: any) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const last6DayData = [...Array(6)].map(
                (_, i) =>
                  info.row.original[`stickers${i + 1}D`] -
                  info.row.original[`stickers${i + 2}D`]
              )
              const today =
                info.row.original.stickers - info.row.original.stickers1D
              const trendChartData = [...last6DayData.reverse(), today]

              return (
                <div style={{ maxHeight: '24px', minWidth: '250px' }}>
                  <AssessmentTrendByIntervalChart
                    labels={dailyTrendByIntervalLabels}
                    chartData={trendChartData}
                    backgroundColor="#6250EF"
                  />
                </div>
              )
            }
          },
          {
            header: () => renderHeaderTooltip('Weekly Trend'),
            id: 'weeklyTrend',
            size: TREND_COLUMN_SIZE,
            cell: (info: any) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const last6WeekData = [...Array(3)].map(
                (_, i) =>
                  info.row.original[`stickers${i + 1}W`] -
                  info.row.original[`stickers${i + 2}W`]
              )
              const thisWeek =
                info.row.original.stickers - info.row.original.stickers1W
              const trendChartData = [...last6WeekData.reverse(), thisWeek]
              return (
                <div style={{ maxHeight: '24px', minWidth: '250px' }}>
                  <AssessmentTrendByIntervalChart
                    labels={weeklyTrendByIntervalLabels}
                    chartData={trendChartData}
                    backgroundColor="#6250EF"
                  />
                </div>
              )
            }
          },
          {
            header: () => renderHeaderTooltip('Monthly Trend'),
            id: 'monthlyTrend',
            size: TREND_COLUMN_SIZE,
            cell: (info: any) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const last6MonthData = [...Array(4)].map(
                (_, i) =>
                  info.row.original[`stickers${i + 1}M`] -
                  info.row.original[`stickers${i + 2}M`]
              )
              const thisMonth =
                info.row.original.stickers - info.row.original.stickers1M
              const trendChartData = [...last6MonthData.reverse(), thisMonth]

              return (
                <div style={{ maxHeight: '24px', minWidth: '250px' }}>
                  <AssessmentTrendByIntervalChart
                    labels={monthlyTrendByIntervalLabels}
                    chartData={trendChartData}
                    backgroundColor="#6250EF"
                  />
                </div>
              )
            }
          }
        ]
      : [
          {
            header: () => renderHeaderTooltip('Daily Trend'),
            id: 'dailyTrend',
            size: TREND_COLUMN_SIZE,
            cell: (info: any) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const last6DayOgData = [...Array(7)].map(
                (_, i) => info.row.original[`stickers${i + 1}D`]
              )
              const cumulativeChartData = [
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                ...last6DayOgData.reverse(),
                info.row.original.stickers
              ]
              return (
                <div style={{ maxHeight: '24px' }}>
                  <AssessmentCumulativeTrendsChart
                    labels={dailyCumulativeTrendLabels}
                    chartData={cumulativeChartData}
                    lineColor="#6250EF"
                    backgroundColor="#6250ef6e"
                  />
                </div>
              )
            }
          },
          {
            header: () => renderHeaderTooltip('Weekly Trend'),
            id: 'weeklyTrend',
            size: TREND_COLUMN_SIZE,
            cell: (info: any) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const last4WeekOgData = [...Array(4)].map(
                (_, i) => info.row.original[`stickers${i + 1}W`]
              )
              const cumulativeChartData = [
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                ...last4WeekOgData.reverse(),
                info.row.original.stickers
              ]
              return (
                <div style={{ maxHeight: '24px' }}>
                  <AssessmentCumulativeTrendsChart
                    labels={weeklyCumulativeTrendLabels}
                    chartData={cumulativeChartData}
                    lineColor="#6250EF"
                    backgroundColor="#6250ef6e"
                  />
                </div>
              )
            }
          },
          {
            header: () => renderHeaderTooltip('Monthly Trend'),
            id: 'monthlyTrend',
            size: TREND_COLUMN_SIZE,
            cell: (info: any) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              const last6MonthOgData = [...Array(5)].map(
                (_, i) => info.row.original[`stickers${i + 1}M`]
              )
              const cumulativeChartData = [
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                ...last6MonthOgData.reverse(),
                info.row.original.stickers
              ]
              return (
                <div style={{ maxHeight: '24px' }}>
                  <AssessmentCumulativeTrendsChart
                    labels={monthlyCumulativeTrendLabels}
                    chartData={cumulativeChartData}
                    lineColor="#6250EF"
                    backgroundColor="#6250ef6e"
                  />
                </div>
              )
            }
          }
        ]
  /**
   * handles table column mapping
   */
  const columnsByView: ColumnType<Database_Procs_TeamActivity_Item>[] = view
    ? [...trendsColumns]
    : [
        {
          header: () => renderHeaderTooltip('Today'),
          accessorFn: ({ stickers = 0, stickers1D = 0 }) =>
            stickers - stickers1D,
          id: 'today',
          size: ACTIVITY_COLUMN_SIZE
        },
        ...thisWeekColumns,
        {
          header: () => renderHeaderTooltip('1 Week ago'),
          id: 'stickers1W',
          accessorFn: ({ stickers1W = 0, stickers2W = 0 }) =>
            stickers1W - stickers2W,
          size: ACTIVITY_COLUMN_SIZE
        },
        {
          header: () => renderHeaderTooltip('2 Weeks ago'),
          id: 'stickers2W',
          accessorFn: ({ stickers2W = 0, stickers3W = 0 }) =>
            stickers2W - stickers3W,
          size: ACTIVITY_COLUMN_SIZE
        },
        {
          header: () => renderHeaderTooltip('3 Weeks ago'),
          id: 'stickers3W',
          accessorFn: ({ stickers3W = 0, stickers4W = 0 }) =>
            stickers3W - stickers4W,
          size: ACTIVITY_COLUMN_SIZE
        }
      ]
  let finalColumns: ColumnType<Database_Procs_TeamActivity_Item>[] = []
  if (sendMessage) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    finalColumns = [
      actioncolumn,
      ...generateScrollableAssessmentCommonColumns<Database_Procs_TeamActivity_Item>(),
      {
        header: () => renderWrappedAssessmentHeader('Est. Time on Task (H)'),
        accessorKey: 'timeOnTask',
        id: 'timeOnTask',
        size: ACTIVITY_COLUMN_SIZE
      },
      ...columnsByView
    ]
  } else {
    const schoolCommonColumns =
      generateScrollableAssessmentCommonColumnsForSchool<Database_Procs_TeamActivity_Item>()

    finalColumns = [
      ...schoolCommonColumns.slice(0, 3),
      {
        header: () => renderHeaderTooltip('Grade'),
        size: ACTIVITY_COLUMN_SIZE,
        accessorKey: 'grade',
        cell: (info) => {
          return renderGradeColumn(info.getValue())
        },
        id: 'grade',
        enableSorting: true
      },
      ...schoolCommonColumns.slice(4),
      ...columnsByView
    ]
  }
  return { finalColumns, highLightIds }
}
export const schoolHidableSelectLabel: Record<string, string> = {
  name: 'Name',
  desc: 'Class Name',
  contactName: 'Teacher',
  grade: 'Grade',
  students: 'Students',
  ...Object.fromEntries(Object.entries(hidableSelectLabel).slice(3))
}
